import {useState} from 'react';
import {ChevronDown, Copy, Pencil} from 'lucide-react';
import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '@kroll-resolver-crisp/ui-components-react';
import type {ApprovedWorkflow} from '../../api/workflows';

export function RoutingPanel({
  detail,
  editable,
  onEdit
}: {
  readonly detail: ApprovedWorkflow;
  readonly editable: boolean;
  readonly onEdit: () => void;
}) {
  const [show, setShow] = useState(false);
  const keywords = detail.routing_keywords
    .split(',')
    .map((keyword) => keyword.trim())
    .filter(Boolean);
  const roots = detail.workflow.approved_root_concept_ids.map(
    (id) => detail.nodes.find((node) => node.concept_id === id)?.path ?? id
  );
  const leaves = detail.nodes.filter(
    (node) => !detail.nodes.some((other) => other.parent_concept_id === node.concept_id)
  );
  const defined = leaves.filter((leaf) =>
    detail.grounding.some((e) => e.concept_id === leaf.concept_id && e.section === 'definition')
  ).length;
  const prompt = `${detail.workflow.key}: ${detail.routing_definition}\nKeywords: ${detail.routing_keywords}\nConcept IDs: ${detail.nodes.map((n) => n.concept_id).join(', ')}`;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid items-start gap-3 lg:grid-cols-2">
        <Card size="sm">
          <CardHeader>
            <CardTitle>Routing</CardTitle>
            <CardDescription>
              What the router reads to decide whether an item belongs here.
            </CardDescription>
            {editable ? (
              <CardAction>
                <Button size="sm" variant="ghost" onClick={onEdit}>
                  <Pencil aria-hidden className="size-4" />
                  Edit
                </Button>
              </CardAction>
            ) : null}
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <p className="text-sm">{detail.routing_definition}</p>
            <div className="flex flex-wrap gap-1.5">
              {keywords.map((keyword) => (
                <span key={keyword} className="bg-muted rounded-full px-2.5 py-0.5 text-xs">
                  {keyword}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card size="sm">
          <CardHeader>
            <CardTitle>Scope</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-muted-foreground">
                {roots.length === 1 ? 'Root concept' : 'Root concepts'}
              </dt>
              <dd>{roots.join('; ')}</dd>
              <dt className="text-muted-foreground">Concepts</dt>
              <dd>{detail.nodes.length}</dd>
              <dt className="text-muted-foreground">Defined</dt>
              <dd>
                {defined} of {leaves.length} leaf concepts have a definition
              </dd>
              <dt className="text-muted-foreground">Router</dt>
              <dd>{detail.enabled ? 'Can assign items to this guidance' : 'Off — skipped'}</dd>
              <dt className="text-muted-foreground">Non-Violative</dt>
              <dd>
                {detail.include_non_violative
                  ? 'Always offered with the routed concepts'
                  : 'Only when routed to'}
              </dd>
            </dl>
          </CardContent>
        </Card>
      </div>
      <Card size="sm">
        <CardHeader>
          <CardTitle>Router prompt</CardTitle>
          <CardDescription>
            This workflow’s block in the vertical-assignment prompt. The router answers with IDs
            from it — names alone never match.
          </CardDescription>
          <CardAction className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void navigator.clipboard?.writeText(prompt)}>
              <Copy aria-hidden className="size-4" />
              Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-expanded={show}
              onClick={() => {
                setShow(!show);
              }}>
              {show ? 'Hide' : 'Show'}
              <ChevronDown
                aria-hidden
                className={`size-4 transition-transform ${show ? 'rotate-180' : ''}`}
              />
            </Button>
          </CardAction>
        </CardHeader>
        {show ? (
          <CardContent>
            <pre className="border-border bg-muted overflow-x-auto rounded border p-4 text-sm">
              {prompt}
            </pre>
          </CardContent>
        ) : null}
      </Card>
    </div>
  );
}
